
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef, useState } from "react";
import type { User } from "@supabase/supabase-js";
import type { SavedSignature } from "@/types/signatures";
import type { DisplayedPendingSignature } from "@/types/signature";
import { EmptySignatureState } from "./signature/EmptySignatureState";
import { SavedSignatureCard } from "./signature/SavedSignatureCard";
import { useSignatureDownload } from "@/hooks/useSignatureDownload";

interface SavedSignaturesProps {
  signatures: SavedSignature[];
  onDelete: (id: string) => void;
  user: User | null;
  loading?: boolean;
  pendingSignatures?: DisplayedPendingSignature[];
}

const INITIAL_SIGNATURES_COUNT = 6;
const LOAD_MORE_COUNT = 3;

export const SavedSignatures = ({ 
  signatures, 
  onDelete, 
  user,
  loading = false,
}: SavedSignaturesProps) => {
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const [displayCount, setDisplayCount] = useState(INITIAL_SIGNATURES_COUNT);
  const { handleDownload } = useSignatureDownload(user);

  const displayedSignatures = signatures.slice(0, displayCount);
  const hasMoreSignatures = signatures.length > displayCount;

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + LOAD_MORE_COUNT);
  };

  const onDownload = async (signature: SavedSignature, format: 'svg' | 'png') => {
    const canvas = canvasRefs.current.get(signature.id);
    handleDownload(signature, format, canvas);
  };

  return (
    <div className="my-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
        {signatures.length === 0 && !loading ? (
          <EmptySignatureState />
        ) : (
          displayedSignatures.map((signature) => (
            <SavedSignatureCard
              key={signature.id}
              signature={signature}
              onDelete={onDelete}
              onDownload={onDownload}
              canvasRef={(el) => {
                if (el) {
                  canvasRefs.current.set(signature.id, el);
                } else {
                  canvasRefs.current.delete(signature.id);
                }
              }}
            />
          ))
        )}
      </div>

      {hasMoreSignatures && (
        <div className="mt-6 flex justify-center">
          <Button
            onClick={handleLoadMore}
            variant="outline"
            className="rounded-full flex items-center gap-2"
          >
            Load more
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

